import { CBTooltip } from '@cb/apricot'
import isEmpty from 'lodash/isEmpty'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Glyph } from '@cb/apricot-react-glyph'
import { DECILE, DashboardPanel, HUNDRED, ReadStudentContext, StyledNHInfoPanel } from '.'
import { ToggleButton } from '../Button'
import { InfoTooltip } from '../InfoTooltip'
import star from '../assets/red_star.png'

const NATIONAL = 'National'
const STATE = 'State'

const StyledHSContext = styled.div`
  position: relative;
  & .chart-callout {
    position: absolute;
    width: 152px;
    padding: 8px;
    background-color: #fedb00;
    border-radius: 3px;
    text-align: center;
    font-size: 13px;
  }

  & .chart-callout::before {
    content: '';
    width: 0px;
    height: 0px;
    border: 0.8em solid transparent;
    position: absolute;
    left: 41%;
    bottom: -18px;
    border-top: 8px solid #fedb00;
  }
`

const BarGraphDiv = styled.div`
  height: 425px;
`

const TitleDiv = styled.div`
  height: 48px;
`

const ConcordedActDiv = styled.div`
  position: absolute;
  margin-top: 0px;
  color: red;
  padding-left: 1px !important;
`

const ConditionalTooltipSpan = styled.span`
  margin-right: 5px;
  &:before {
    color: black !important;
  }

  &:hover::after {
    background-color: ${(props) => (props.isBie ? 'initial' : '#fedb00 !important')};
  }
`

const StyledGlyph = styled(Glyph)`
  font-size: 4px !important;
  margin-right: 4px !important;
  margin-bottom: 1px !important;
`

interface ChartComponentProps {
  overrideChartUnitsPref: boolean
  tempChartUnitsSelection: string
  instPrefChartDisplayUnits: string
  onChartUnitsChange: any
  isPOBox: any
  isBieSchool: any
  percentTakingSAT?: number
  setShowNational: Function
  showNational: boolean
  readStudentContext: ReadStudentContext
  isInteractive: boolean
}

const SchoolNameDiv = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`

const BieSchoolDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0px 10px;
`

const BieSchoolText = styled.h6`
  color: red;
  line-height: 1em;
`

const StyledHighShoolPanel = styled.div`
  & h6 {
    margin-bottom: 5px;
  }
  & .col-xs-12:nth-child(even) .chart-high-school {
    padding-left: 5px;
  }
  & .col-xs-12:nth-child(odd) .chart-high-school {
    padding-right: 5px;
  }
  & .key-value-block {
    margin-bottom: 5px;
  }
`

export const getRuralDescription = (description: string) => {
  if (description) {
    const char = parseInt(description.charAt(0))

    if (!isNaN(char) && description.length > 3) {
      return description.substring(3)
    }

    return description
  }

  return 'NA'
}

export const ChartComponent: React.FC<ChartComponentProps> = (props) => {
  const {
    overrideChartUnitsPref,
    tempChartUnitsSelection,
    instPrefChartDisplayUnits,
    onChartUnitsChange,
    isPOBox,
    isBieSchool,
    percentTakingSAT,
    setShowNational,
    showNational,
    readStudentContext,
    isInteractive = true,
  } = props

  const poBoxTooltipRef = useRef()
  const percentSatTooltipRef = useRef()
  const bieSchoolTooltipRef = useRef()

  useEffect(() => {
    const poBoxTooltip = CBTooltip({
      markup: false,
      elem: poBoxTooltipRef.current,
      placement: 'top',
      title: 'Neighborhood challenge generated from PO Box.',
    })

    const percentSatTooltip = CBTooltip({
      markup: false,
      elem: percentSatTooltipRef.current,
      placement: 'top',
      title: 'Less than 25% of Seniors taking SAT.',
    })

    const bieSchoolTooltip = CBTooltip({
      markup: false,
      elem: bieSchoolTooltipRef.current,
      placement: 'top',
      title: 'Part of the Bureau of Indian Education school system.',
    })
  })

  const isStudentDisplayScoreAvailable = (studentContext: ReadStudentContext) => {
    return !isEmpty(studentContext.display_score) && studentContext.display_score !== '0'
  }

  const isRural = /^.{3}Rural.*/.test(readStudentContext.rural)

  let chartUnitsToggleText
  if (overrideChartUnitsPref) {
    chartUnitsToggleText = tempChartUnitsSelection
  } else {
    chartUnitsToggleText = instPrefChartDisplayUnits
  }
  const chartUnitsToggleProps = {
    selectedText: chartUnitsToggleText,
    name: 'decile-hundred',
    onChange: onChartUnitsChange,
    buttonOneSelection: DECILE,
    buttonTwoSelection: HUNDRED,
  }

  const nationalStateToggleProps = {
    selectedText: showNational ? NATIONAL : STATE,
    name: 'national-state',
    onChange: (choice) => {
      if (choice === NATIONAL) {
        setShowNational(true)
      } else {
        setShowNational(false)
      }
    },
    buttonOneSelection: NATIONAL,
    buttonTwoSelection: STATE,
  }

  return (
    <>
      <div id="panel1">
        <div className={isInteractive ? 'layout-panel' : 'layout-panel-static'}>
          <div className="row">
            <BarGraphDiv className="col-xs-12 col-md-6 block1">
              <TitleDiv className="row display-flex align-items-center justify-content-between">
                <h5 className="">Context Overview</h5>
                <div className="display-flex justify-content-end cb-margin-right-8">
                  {isInteractive && <ToggleButton {...chartUnitsToggleProps} />}
                </div>
              </TitleDiv>
              <div
                id="overallLevelSummaryEnvironmentalContext"
                className="d3Chart"
                style={{
                  marginTop: 'auto',
                  marginBottom:
                    isStudentDisplayScoreAvailable(readStudentContext) &&
                    percentTakingSAT > 0 &&
                    !isPOBox
                      ? '1.5em'
                      : '',
                }}
              ></div>
              {isPOBox && (
                <div className="key-value-block2">
                  <div className="value-block">
                    <div id="is-po-box">
                      <div style={{ display: 'flex' }}>
                        {isInteractive && (
                          <ConditionalTooltipSpan
                            className="cb-glyph cb-exclamation cb-glyph-circular tooltip-info"
                            tabIndex={0}
                            role="link"
                            ref={poBoxTooltipRef}
                            id="pobox"
                            data-toggle="tooltip"
                            aria-label="tooltip"
                          ></ConditionalTooltipSpan>
                        )}
                        <div role="tooltip" className="cb-tooltip hidden" aria-hidden="true">
                          <div className="cb-tooltip-inner"></div>
                        </div>
                        <p className="no-slate-margin-top">
                          Applicant neighborhood generated from PO Box
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </BarGraphDiv>
            <BarGraphDiv className="col-xs-12 col-md-6 block2">
              <TitleDiv className="row display-flex align-items-center">
                <h5 style={{ marginLeft: '10px' }} className="text-SAT-score">
                  SAT Score in High School Context
                </h5>
              </TitleDiv>

              {readStudentContext.score_source === 'ACT' && (
                <ConcordedActDiv className="cb-roboto-italic cb-align-left">
                  Concorded ACT Score
                </ConcordedActDiv>
              )}

              <div
                id="SATScoreInHSContextContainer"
                style={{
                  marginTop: 'auto',
                  marginBottom:
                    isPOBox &&
                    !(isStudentDisplayScoreAvailable(readStudentContext) && percentTakingSAT > 0)
                      ? '1.5em'
                      : '',
                  width: '100%',
                  paddingLeft: '12px',
                }}
              >
                <>
                  <StyledHSContext id="SATScoreInHSContext" className="d3Chart"></StyledHSContext>
                  {isStudentDisplayScoreAvailable(readStudentContext) && percentTakingSAT > 0 && (
                    <div className="key-value-block2">
                      <div className="value-block">
                        <div id="senior-percentage-exclamation">
                          <div style={{ display: 'flex' }}>
                            {percentTakingSAT < 25 && (
                              <>
                                <ConditionalTooltipSpan
                                  className="cb-glyph cb-exclamation cb-glyph-circular tooltip-info"
                                  tabIndex={0}
                                  ref={percentSatTooltipRef}
                                  title=""
                                  role="link"
                                  id="senior-percent-low"
                                  data-toggle="tooltip"
                                  aria-label="tooltip"
                                ></ConditionalTooltipSpan>
                                <div
                                  role="tooltip"
                                  className="cb-tooltip hidden"
                                  aria-hidden="true"
                                >
                                  <div className="cb-tooltip-inner"></div>
                                </div>
                              </>
                            )}
                            <p className="no-slate-margin-top">
                              {percentTakingSAT}% of seniors take SAT
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              </div>
            </BarGraphDiv>
          </div>
        </div>
      </div>

      <div id="panel2">
        <div className="row">
          <div className="col-xs-12">
            <div className={isInteractive ? 'layout-panel' : 'layout-panel-static'}>
              <SchoolNameDiv>
                <h5>{readStudentContext.org_nm}</h5>
                {isBieSchool && (
                  <BieSchoolDiv className="value-block">
                    {isInteractive && (
                      <>
                        <ConditionalTooltipSpan
                          className="cb-glyph cb-exclamation cb-glyph-circular tooltip-info"
                          tabIndex={0}
                          ref={bieSchoolTooltipRef}
                          isBie={true}
                          role="link"
                          id="bie-school"
                          data-toggle="tooltip"
                          aria-label="tooltip"
                          data-original-title="Part of the Bureau of Indian Education school system"
                        ></ConditionalTooltipSpan>
                        <div role="tooltip" className="cb-tooltip hidden" aria-hidden="true">
                          <div className="cb-tooltip-inner"></div>
                        </div>
                      </>
                    )}
                    <BieSchoolText>BIE SCHOOL</BieSchoolText>
                  </BieSchoolDiv>
                )}
              </SchoolNameDiv>
              <StyledHighShoolPanel className="row">
                <div className="col-xs-12 col-md-6 block1">
                  <div className="chart-high-school">
                    <h6>General Information</h6>
                    <div className="flex-box">
                      <div className="key-value-block">
                        <p>Locale:&nbsp;&nbsp;&nbsp;</p>
                        <div className="value-block">
                          <strong style={{ marginRight: isRural && isInteractive ? '6px' : '3px' }}>
                            {getRuralDescription(readStudentContext.rural)}
                            {isRural && (
                              <span className="custom-star" aria-hidden="true">
                                <img src={star} alt="red-star" width="20" height="20" />
                              </span>
                            )}
                          </strong>
                          {isInteractive && (
                            <InfoTooltip textContent="This measure is based on the high school’s location, and relies on the National Center for Education Statistics (NCES) system of classifying geographic areas into 4 categories: City, Suburban, Town, and Rural." />
                          )}
                        </div>
                      </div>
                      <div className="key-value-block">
                        <p>Senior Class Size:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {isNaN(parseInt(readStudentContext.a_sr_class_size, 10))
                              ? 'NA'
                              : readStudentContext.a_sr_class_size}
                          </strong>
                          {isInteractive && (
                            <InfoTooltip textContent="This measure is based on a 3-year average of senior class size." />
                          )}
                        </div>
                      </div>
                      <div className="key-value-block">
                        <p>Free/Reduced Lunch:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {isNaN(parseFloat(readStudentContext.a_avg_frl_pct))
                              ? 'NA'
                              : parseFloat(readStudentContext.a_avg_frl_pct).toFixed(0) + '%'}{' '}
                          </strong>
                          {readStudentContext.frl_flag === '1' && (
                            <StyledGlyph
                              circleSize="24"
                              size="12"
                              decorative={true}
                              circular={true}
                              color={'red1'}
                            />
                          )}
                          {isInteractive && (
                            <InfoTooltip textContent="Percent of students eligible for Free and Reduced Lunch. Available for public schools only, based on a 3-year average. A solid red dot icon appears when % eligible has been estimated using direct certification counts, using USDA guidelines." />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xs-12 col-md-6 block2">
                  <div className="chart-high-school">
                    <h6>AP Opportunity</h6>
                    <div className="flex-box">
                      <div className="key-value-block">
                        <p>Seniors Taking AP:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {isNaN(parseInt(readStudentContext.a_pct_taking_ap, 10))
                              ? 'NA'
                              : readStudentContext.a_pct_taking_ap + '%'}
                          </strong>
                          {isInteractive && (
                            <InfoTooltip textContent="Three-year average of the percentage of the senior class who have taken at least one AP Exam." />
                          )}
                        </div>
                      </div>
                      <div className="key-value-block">
                        <p>Average AP Exams Taken:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {isNaN(parseFloat(readStudentContext.adv_aptaken))
                              ? 'NA'
                              : parseFloat(readStudentContext.adv_aptaken).toFixed(2)}
                          </strong>
                          {isInteractive && (
                            <InfoTooltip textContent="3-year average of the number of AP Exams taken by seniors from the high school who took at least one AP Exam." />
                          )}
                        </div>
                      </div>
                      <div className="key-value-block">
                        <p>Average AP Score:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {isNaN(parseFloat(readStudentContext.adv_avgapscore))
                              ? 'NA'
                              : parseFloat(readStudentContext.adv_avgapscore).toFixed(2)}
                          </strong>
                          {isInteractive && (
                            <InfoTooltip textContent="Three-year average of AP Exam scores across college-bound seniors from the high school who took AP Exam(s)." />
                          )}
                        </div>
                      </div>
                      <div className="key-value-block">
                        <p>Unique Exams Administered:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {isNaN(parseFloat(readStudentContext.adv_aptestavail))
                              ? 'NA'
                              : parseFloat(readStudentContext.adv_aptestavail).toFixed(0)}
                          </strong>
                          {isInteractive && (
                            <InfoTooltip textContent="Three-year average of the number of unique AP Exams taken by college-bound seniors at the high school." />
                          )}
                        </div>
                      </div>
                      <div className="key-value-block">
                        <p>AP Capstone School:</p>
                        <div className="value-block">
                          <strong className="marginRight5">
                            {readStudentContext.ddv_apcapstone == '1' ? 'Yes' : 'No'}
                          </strong>
                          {isInteractive && (
                            <InfoTooltip textContent="High school approved to offer AP Capstone Diploma program." />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </StyledHighShoolPanel>
            </div>
          </div>
        </div>
      </div>

      <div id="panel3">
        <div className={isInteractive ? 'layout-panel' : 'layout-panel-static'}>
          <div className="panel3Chart2">
            <div className="row" style={{ display: 'flex', alignItems: 'center' }}>
              <div className="col-xs-7">
                <h5 className="cb-align-left">Neighborhood and High School Indicators</h5>
              </div>

              <div className="col-xs-5">
                <div className="display-flex justify-content-end cb-margin-right-8">
                  {isInteractive && <ToggleButton {...nationalStateToggleProps} />}
                </div>
              </div>
            </div>
            {/* end row for chart header */}

            <div className="row">
              <div className="col-md-8">
                <div id="panel3Chart2" className="d3Chart"></div>
              </div>
              <div className="col-md-4">
                <div id="panel3Chart3" className="d3Chart"></div>
              </div>
            </div>
            {/* end row for charts */}
            {/* start Exploratory Features  */}
            <StyledNHInfoPanel className="">
              <div className="nh-info-title value-block">
                {isInteractive && (
                  <InfoTooltip textContent="Beta features added in response to COVID-19 impacts." />
                )}
                <span className="nh-info-desc">Additional Neighborhood Context</span>
              </div>
              <div className="nh-info-panels">
                <DashboardPanel
                  isInteractive={isInteractive}
                  title="Social Vulnerability Index:"
                  value={readStudentContext.a_nh_svi_ptile}
                  width="calc(50% - 6px)"
                  tooltipText="CDC measure of community resilience to external stresses (e.g., natural disasters or pandemics). Higher numbers represent more challenging environments with less resilience to external stresses."
                />
                <DashboardPanel
                  isInteractive={isInteractive}
                  title="Internet Access Variable:"
                  value={
                    readStudentContext.a_nh_share_no_inter
                      ? `${readStudentContext.a_nh_share_no_inter}%`
                      : 'NA'
                  }
                  width="calc(50% - 6px)"
                  tooltipText="Percent of households with children under 18 with no computer or no internet subscription (ACS). Higher percentages indicate areas with lower computer/internet access."
                />
              </div>
            </StyledNHInfoPanel>
            {/* end Beta Features  */}
          </div>
        </div>
      </div>
    </>
  )
}
